import { PsaFixtureType } from "@CommonTypes/psa/FixtureRecord";
import type { PreviewPsaFixture, PreviewPsaSegmentBounds } from "./previewTypes";

export enum FixtureCanCombine {
  NONE = 0,
  BOTH = 1,
  LEFT = 2,
  RIGHT = 3,
}

export const SECTION_X_TOLERANCE = 1;

/**
 * Default minimum overlap ratio used when importing PSA files.
 * A shelf that crosses a section boundary is NOT split if its overlap into the
 * next section is ≤ this fraction of that section's width.
 */
export const DEFAULT_MIN_SECTION_OVERLAP_RATIO = 0.25;

type SectionBoundary = {
  left: number;
  right: number;
};

export type PreviewPsaSectioningOptions = {
  planogramWidth?: number | null;
  segments?: PreviewPsaSegmentBounds[];
  /**
   * Minimum overlap ratio (as a fraction of the **next** section's width) required for a shelf
   * that crosses a section boundary to be split into two pieces.
   *
   * When a shelf's extension past the section boundary divided by the next section's width
   * is ≤ this value, the shelf is NOT split – it stays entirely within its originating section.
   *
   * For example, `DEFAULT_MIN_SECTION_OVERLAP_RATIO` (0.25) means "do not split if the overlap
   * is ≤ 25% of the next section's width".
   * Defaults to `DEFAULT_MIN_SECTION_OVERLAP_RATIO` when not specified. Pass `0` explicitly
   * to split on any overlap larger than `SECTION_X_TOLERANCE`.
   */
  minSectionOverlapRatio?: number;
};

type SectionBoundaryResult = {
  boundaries: SectionBoundary[];
  containedStartXs: Set<number>;
  floatingStartXs: Set<number>;
};

export const normalizePsaShelfY = (fixtureY: number, minY: number) =>
  minY < 0 ? fixtureY - minY : fixtureY;

const getMinFixtureY = (fixtures: PreviewPsaFixture[]) =>
  fixtures.reduce((minY, fixture) => Math.min(minY, fixture.y), fixtures[0]?.y ?? 0);

const ensureFixtureName = (fixture: PreviewPsaFixture) =>
  fixture.name === "NONE" || !fixture.name ? fixture.desc1 : fixture.name;

const sortNumbersAscending = (left: number, right: number) => left - right;

const isWithinSectionTolerance = (left: number, right: number) =>
  Math.abs(left - right) <= SECTION_X_TOLERANCE;

const getClusteredValues = (values: number[]) => {
  if (values.length === 0) {
    return [];
  }

  const sortedValues = [...values].sort(sortNumbersAscending);
  const clusteredValues: number[] = [sortedValues[0] ?? 0];
  let clusterStart = sortedValues[0] ?? 0;

  for (let i = 1; i < sortedValues.length; i += 1) {
    const value = sortedValues[i];
    if (value <= clusterStart + SECTION_X_TOLERANCE) {
      continue;
    }
    clusteredValues.push(value);
    clusterStart = value;
  }

  return clusteredValues;
};

const sectionContains = (container: SectionBoundary, section: SectionBoundary) =>
  container.left <= section.left + SECTION_X_TOLERANCE &&
  container.right >= section.right - SECTION_X_TOLERANCE;

const getSectionWidth = (section: SectionBoundary) => section.right - section.left;

const isStrictlyWider = (candidate: SectionBoundary, target: SectionBoundary) =>
  candidate.left < target.left - SECTION_X_TOLERANCE ||
  candidate.right > target.right + SECTION_X_TOLERANCE;

const getPlanogramWidth = (options?: PreviewPsaSectioningOptions) => {
  const planogramWidth = Number(options?.planogramWidth);
  return Number.isFinite(planogramWidth) && planogramWidth > SECTION_X_TOLERANCE
    ? planogramWidth
    : null;
};

const buildBoundariesFromSegments = (segments: PreviewPsaSegmentBounds[]): SectionBoundary[] => {
  const validSegments = segments
    .filter(
      (segment) =>
        Number.isFinite(segment.x) &&
        Number.isFinite(segment.width) &&
        segment.width > SECTION_X_TOLERANCE &&
        Number.isFinite(segment.height) &&
        segment.height > 0,
    )
    .sort((left, right) => left.x - right.x || left.width - right.width);

  return validSegments.map((segment) => ({
    left: segment.x,
    right: segment.x + segment.width,
  }));
};

const fixtureSpansBoundary = (fixture: PreviewPsaFixture, boundary: SectionBoundary) =>
  isWithinSectionTolerance(fixture.x, boundary.left) &&
  Math.abs(fixture.x + fixture.width - boundary.right) <= SECTION_X_TOLERANCE;

const buildPrimaryTiledSectionBoundaries = (
  fixtures: PreviewPsaFixture[],
  sectionStarts: number[],
  planogramWidth: number,
): Pick<SectionBoundaryResult, "boundaries" | "floatingStartXs"> | null => {
  const candidateWidths = getClusteredValues(
    fixtures
      .map((fixture) => fixture.width)
      .filter((width) => Number.isFinite(width) && width > SECTION_X_TOLERANCE * 5),
  ).sort((left, right) => right - left);

  for (const candidateWidth of candidateWidths) {
    const tileCount = Math.round(planogramWidth / candidateWidth);
    if (
      tileCount < 2 ||
      Math.abs(tileCount * candidateWidth - planogramWidth) > SECTION_X_TOLERANCE
    ) {
      continue;
    }

    const origins = sectionStarts
      .filter((start) => start >= -SECTION_X_TOLERANCE)
      .sort((left, right) => Math.abs(left) - Math.abs(right));

    for (const origin of origins) {
      const boundaries: SectionBoundary[] = [];
      let allTilesHaveSpanningFixture = true;

      for (let tileIndex = 0; tileIndex < tileCount; tileIndex += 1) {
        const boundary = {
          left: origin + tileIndex * candidateWidth,
          right: origin + (tileIndex + 1) * candidateWidth,
        };

        if (!fixtures.some((fixture) => fixtureSpansBoundary(fixture, boundary))) {
          allTilesHaveSpanningFixture = false;
          break;
        }

        boundaries.push(boundary);
      }

      if (!allTilesHaveSpanningFixture) {
        continue;
      }

      const planogramRight = origin + planogramWidth;
      const floatingStartXs = new Set(
        sectionStarts.filter(
          (start) =>
            start < origin - SECTION_X_TOLERANCE || start >= planogramRight - SECTION_X_TOLERANCE,
        ),
      );

      return { boundaries, floatingStartXs };
    }
  }

  return null;
};

const buildSectionBoundaries = (
  fixtures: PreviewPsaFixture[],
  options: PreviewPsaSectioningOptions = {},
): SectionBoundaryResult => {
  const sectionStarts = getClusteredValues(fixtures.map((fixture) => fixture.x));
  if (options.segments && options.segments.length > 0) {
    const segmentBoundaries = buildBoundariesFromSegments(options.segments);
    if (segmentBoundaries.length > 0) {
      // PSA segment records are canonical section boundaries; floating detection
      // below checks fixtures against these spans instead of clustering starts.
      return {
        boundaries: segmentBoundaries,
        containedStartXs: new Set(),
        floatingStartXs: new Set(),
      };
    }
  }

  const planogramWidth = getPlanogramWidth(options);
  if (planogramWidth !== null) {
    const primaryTiledSections = buildPrimaryTiledSectionBoundaries(
      fixtures,
      sectionStarts,
      planogramWidth,
    );
    if (primaryTiledSections) {
      return {
        boundaries: primaryTiledSections.boundaries,
        containedStartXs: new Set(),
        floatingStartXs: primaryTiledSections.floatingStartXs,
      };
    }
  }

  const fixturesBySectionStart = new Map<number, PreviewPsaFixture[]>();
  const fixtureCountBySectionStart = new Map<number, number>();
  const boundaries = sectionStarts
    .map((sectionStart) => {
      const fixturesAtStart = fixtures.filter((fixture) =>
        isWithinSectionTolerance(fixture.x, sectionStart),
      );
      fixturesBySectionStart.set(sectionStart, fixturesAtStart);
      fixtureCountBySectionStart.set(sectionStart, fixturesAtStart.length);

      if (fixturesAtStart.length === 0) {
        return null;
      }

      const minRight = fixturesAtStart.reduce(
        (acc, fixture) => Math.min(acc, fixture.x + fixture.width),
        Number.POSITIVE_INFINITY,
      );
      const maxRight = fixturesAtStart.reduce(
        (acc, fixture) => Math.max(acc, fixture.x + fixture.width),
        Number.NEGATIVE_INFINITY,
      );
      const minRightIsABoundary = sectionStarts.some(
        (start) => Math.abs(start - minRight) <= SECTION_X_TOLERANCE,
      );
      const sectionRight =
        !minRightIsABoundary && maxRight > minRight + SECTION_X_TOLERANCE ? maxRight : minRight;

      if (sectionRight <= sectionStart + SECTION_X_TOLERANCE) {
        return null;
      }

      return { left: sectionStart, right: sectionRight } satisfies SectionBoundary;
    })
    .filter((section): section is SectionBoundary => section !== null)
    .sort((left, right) => sortNumbersAscending(left.left, right.left));

  const containedStartXs = new Set<number>();
  const nonContainedBoundaries = boundaries.filter((section, index) => {
    const containingBoundary = boundaries.find(
      (candidate, candidateIndex) =>
        candidateIndex !== index &&
        sectionContains(candidate, section) &&
        isStrictlyWider(candidate, section),
    );

    if (containingBoundary) {
      const containedFixtures = fixturesBySectionStart.get(section.left) ?? [];
      const subFixtureCount = fixtureCountBySectionStart.get(section.left) ?? 0;
      const containerFixtureCount = fixtureCountBySectionStart.get(containingBoundary.left) ?? 0;
      const containedFixturesAreOnlyRods =
        containedFixtures.length > 0 &&
        containedFixtures.every((fixture) => fixture.type === PsaFixtureType.ROD);

      const rightEdgeDiffers =
        Math.abs(section.right - containingBoundary.right) > SECTION_X_TOLERANCE;
      const hasMultipleFixtures = subFixtureCount >= 2;
      const containerIsDominant = containerFixtureCount >= subFixtureCount;

      if (
        rightEdgeDiffers &&
        hasMultipleFixtures &&
        containerIsDominant &&
        !containedFixturesAreOnlyRods
      ) {
        containedStartXs.add(section.left);
      }
      return false;
    }

    return true;
  });

  const dedupedBoundaries: SectionBoundary[] = [];
  for (const boundary of nonContainedBoundaries) {
    const previousBoundary = dedupedBoundaries[dedupedBoundaries.length - 1];
    if (
      previousBoundary &&
      isWithinSectionTolerance(previousBoundary.left, boundary.left) &&
      isWithinSectionTolerance(previousBoundary.right, boundary.right)
    ) {
      continue;
    }
    dedupedBoundaries.push(boundary);
  }

  const sequentialBoundaries: SectionBoundary[] = [];
  for (let i = 0; i < dedupedBoundaries.length; i += 1) {
    const boundary = dedupedBoundaries[i];
    const previousBoundary = sequentialBoundaries[sequentialBoundaries.length - 1];
    if (!previousBoundary) {
      sequentialBoundaries.push(boundary);
      continue;
    }

    const SIGNIFICANT_OVERLAP = SECTION_X_TOLERANCE * 5;
    const startsInsidePreviousSection =
      boundary.left < previousBoundary.right - SIGNIFICANT_OVERLAP;

    if (startsInsidePreviousSection) {
      const nextDedupedBoundary = dedupedBoundaries[i + 1];
      const droppedRightReachesNextBay =
        !nextDedupedBoundary ||
        Math.abs(boundary.right - nextDedupedBoundary.left) <= SECTION_X_TOLERANCE;
      if (droppedRightReachesNextBay) {
        previousBoundary.right = Math.max(previousBoundary.right, boundary.right);
      }
      continue;
    }

    sequentialBoundaries.push(boundary);
  }

  if (sequentialBoundaries.length > 0) {
    return { boundaries: sequentialBoundaries, containedStartXs, floatingStartXs: new Set() };
  }

  const fallbackLeft = fixtures.reduce(
    (minimumLeft, fixture) => Math.min(minimumLeft, fixture.x),
    Number.POSITIVE_INFINITY,
  );
  const fallbackRight = fixtures.reduce(
    (maximumRight, fixture) => Math.max(maximumRight, fixture.x + fixture.width),
    Number.NEGATIVE_INFINITY,
  );

  if (Number.isFinite(fallbackLeft) && Number.isFinite(fallbackRight)) {
    return {
      boundaries: [{ left: fallbackLeft, right: fallbackRight }],
      containedStartXs,
      floatingStartXs: new Set(),
    };
  }

  return { boundaries: [], containedStartXs, floatingStartXs: new Set() };
};

const splitShelfAcrossSectionBoundary = (
  shelf: PreviewPsaFixture,
  sectionBoundaryRight: number,
  nextSectionWidth: number,
  minOverlapRatio: number = 0,
): PreviewPsaFixture | null => {
  const originalWidth = shelf.width;

  if (sectionBoundaryRight <= shelf.x + SECTION_X_TOLERANCE) {
    shelf.can_combine = shelf.can_combine ?? FixtureCanCombine.NONE;
    return null;
  }

  const widthWithinCurrentSection = sectionBoundaryRight - shelf.x;
  const overlapWidth = originalWidth - widthWithinCurrentSection;

  // Absorb sub-tolerance overlaps that are likely floating-point artifacts.
  if (overlapWidth <= SECTION_X_TOLERANCE) {
    shelf.can_combine = shelf.can_combine ?? FixtureCanCombine.NONE;
    return null;
  }

  // Degenerate next sections cannot provide a meaningful overlap ratio, so keep
  // the shelf in its current section instead of manufacturing a split.
  const overlapRatio = nextSectionWidth > 0 ? overlapWidth / nextSectionWidth : 0;

  if (overlapRatio <= minOverlapRatio) {
    shelf.can_combine = shelf.can_combine ?? FixtureCanCombine.NONE;
    return null;
  }

  if (shelf.can_combine !== FixtureCanCombine.LEFT) {
    shelf.can_combine = FixtureCanCombine.RIGHT;
  }

  shelf.split_index ||= 1;
  shelf.width = widthWithinCurrentSection;

  return {
    ...shelf,
    x: sectionBoundaryRight,
    width: originalWidth - widthWithinCurrentSection,
    split_index: shelf.split_index + 1,
    can_combine: FixtureCanCombine.BOTH,
  };
};

const findSectionIndexForFixture = (
  sectionBoundaries: SectionBoundary[],
  fixture: PreviewPsaFixture,
) => {
  const fixtureLeft = fixture.x;
  const fixtureRight = fixture.x + fixture.width;
  const targetCoordinate = fixtureLeft + SECTION_X_TOLERANCE;

  for (let i = 0; i < sectionBoundaries.length; i += 1) {
    const sectionBoundary = sectionBoundaries[i];
    if (
      targetCoordinate >= sectionBoundary.left - SECTION_X_TOLERANCE &&
      targetCoordinate < sectionBoundary.right - SECTION_X_TOLERANCE &&
      fixtureRight <= sectionBoundary.right + SECTION_X_TOLERANCE
    ) {
      return i;
    }
  }

  for (let i = 0; i < sectionBoundaries.length; i += 1) {
    const sectionBoundary = sectionBoundaries[i];
    if (
      targetCoordinate >= sectionBoundary.left - SECTION_X_TOLERANCE &&
      targetCoordinate < sectionBoundary.right - SECTION_X_TOLERANCE
    ) {
      return i;
    }
  }

  for (let i = 0; i < sectionBoundaries.length; i += 1) {
    const sectionBoundary = sectionBoundaries[i];
    if (
      fixtureRight > sectionBoundary.left + SECTION_X_TOLERANCE &&
      fixtureLeft < sectionBoundary.right - SECTION_X_TOLERANCE
    ) {
      return i;
    }
  }

  let closestSectionIndex = 0;
  let minimumDistance = Number.POSITIVE_INFINITY;
  for (let i = 0; i < sectionBoundaries.length; i += 1) {
    const distanceToLeft = Math.abs(sectionBoundaries[i]?.left - fixtureLeft);
    if (distanceToLeft < minimumDistance) {
      minimumDistance = distanceToLeft;
      closestSectionIndex = i;
    }
  }
  return closestSectionIndex;
};

const getCompositeShelfRowKey = (fixture: PreviewPsaFixture) =>
  fixture.type === PsaFixtureType.ROD
    ? [fixture.type, Math.floor(Number(fixture.y) / 5)].join("|")
    : [
        fixture.type,
        Number(fixture.y).toFixed(2),
        Number(fixture.height).toFixed(2),
        Number(fixture.depth).toFixed(2),
        Number(fixture.notch_offset).toFixed(2),
      ].join("|");

const getPsaRecordIndices = (fixtures: PreviewPsaFixture[]) =>
  [
    ...new Set(
      fixtures
        .map((fixture) => fixture.psa_record_index)
        .filter((recordIndex): recordIndex is number => Number.isFinite(recordIndex)),
    ),
  ].sort(sortNumbersAscending);

const isCompositeShelfRow = (fixtures: PreviewPsaFixture[], sectionBoundary: SectionBoundary) => {
  if (fixtures.length < 2) {
    return false;
  }

  if (fixtures.every((fixture) => fixture.type === PsaFixtureType.ROD)) {
    return true;
  }

  if (
    fixtures.some(
      (fixture) =>
        fixture.type !== PsaFixtureType.REGULAR &&
        fixture.type !== PsaFixtureType.BAR &&
        fixture.type !== PsaFixtureType.ROD,
    )
  ) {
    return false;
  }

  const sectionWidth = getSectionWidth(sectionBoundary);
  if (fixtures.some((fixture) => fixture.width >= sectionWidth - SECTION_X_TOLERANCE)) {
    return false;
  }

  const sortedFixtures = [...fixtures].sort((left, right) => left.x - right.x);
  const firstFixture = sortedFixtures[0];
  if (!firstFixture) {
    return false;
  }

  const rowLeft = firstFixture.x;
  let rowRight = firstFixture.x + firstFixture.width;

  if (rowLeft < sectionBoundary.left - SECTION_X_TOLERANCE) {
    return false;
  }

  for (let i = 1; i < sortedFixtures.length; i += 1) {
    const fixture = sortedFixtures[i];
    if (fixture.x > rowRight + SECTION_X_TOLERANCE) {
      return false;
    }
    rowRight = Math.max(rowRight, fixture.x + fixture.width);
  }

  return (
    rowLeft <= sectionBoundary.left + SECTION_X_TOLERANCE &&
    rowRight >= sectionBoundary.right - SECTION_X_TOLERANCE &&
    rowRight <= sectionBoundary.right + SECTION_X_TOLERANCE
  );
};

const collapseCompositeShelfRows = (
  fixtures: PreviewPsaFixture[],
  sectionBoundary: SectionBoundary,
): PreviewPsaFixture[] => {
  const fixturesByRow = new Map<string, PreviewPsaFixture[]>();
  for (const fixture of fixtures) {
    const rowKey = getCompositeShelfRowKey(fixture);
    fixturesByRow.set(rowKey, [...(fixturesByRow.get(rowKey) ?? []), fixture]);
  }

  const collapsedFixtures: PreviewPsaFixture[] = [];
  for (const group of fixturesByRow.values()) {
    if (!isCompositeShelfRow(group, sectionBoundary)) {
      collapsedFixtures.push(...group);
      continue;
    }

    const sortedGroup = [...group].sort((left, right) => left.x - right.x);
    const firstFixture = sortedGroup[0];
    if (!firstFixture) {
      continue;
    }

    const sourcePsaRecordIndices = getPsaRecordIndices(sortedGroup);
    collapsedFixtures.push({
      ...firstFixture,
      x: sectionBoundary.left,
      width: getSectionWidth(sectionBoundary),
      can_combine: FixtureCanCombine.NONE,
      section_x: sectionBoundary.left,
      source_psa_record_indices: sourcePsaRecordIndices,
    });
  }

  return collapsedFixtures.sort(
    (left, right) =>
      right.shelf_y - left.shelf_y ||
      left.x - right.x ||
      left.width - right.width ||
      (left.name ?? "").localeCompare(right.name ?? ""),
  );
};

export const splitMergedSectionAndNumber = (
  fixtures: PreviewPsaFixture[],
  options: PreviewPsaSectioningOptions = {},
): { fixtures: PreviewPsaFixture[]; floatingSectionNums: Set<number> } => {
  if (fixtures.length === 0) {
    return { fixtures: [], floatingSectionNums: new Set() };
  }

  const minY = getMinFixtureY(fixtures);
  const normalizedFixtures = fixtures.map((fixture) => ({
    ...fixture,
    name: ensureFixtureName(fixture),
    shelf_y: normalizePsaShelfY(fixture.y, minY),
  }));

  const {
    boundaries: sectionBoundaries,
    containedStartXs,
    floatingStartXs,
  } = buildSectionBoundaries(normalizedFixtures, options);
  if (sectionBoundaries.length === 0) {
    return { fixtures: [], floatingSectionNums: new Set() };
  }

  const explicitFloatingStartXsArray = [...containedStartXs, ...floatingStartXs];
  const explicitFloatingFixtures: PreviewPsaFixture[] = [];
  const normalFixtures: PreviewPsaFixture[] = [];
  for (const fixture of normalizedFixtures) {
    if (
      explicitFloatingStartXsArray.some((startX) => isWithinSectionTolerance(fixture.x, startX))
    ) {
      explicitFloatingFixtures.push(fixture);
    } else {
      normalFixtures.push(fixture);
    }
  }

  if (options.segments && options.segments.length > 0) {
    const validSegments = options.segments.filter(
      (segment) =>
        Number.isFinite(segment.x) &&
        Number.isFinite(segment.width) &&
        segment.width > 0 &&
        Number.isFinite(segment.height) &&
        segment.height > 0,
    );
    if (validSegments.length > 0) {
      for (let i = normalFixtures.length - 1; i >= 0; i -= 1) {
        const fixture = normalFixtures[i];
        const isWithinAnySegment = validSegments.some(
          (segment) =>
            fixture.x >= segment.x - SECTION_X_TOLERANCE &&
            fixture.x <= segment.x + segment.width + SECTION_X_TOLERANCE &&
            fixture.y <= segment.height,
        );
        if (!isWithinAnySegment) {
          normalFixtures.splice(i, 1);
          explicitFloatingFixtures.push(fixture);
          explicitFloatingStartXsArray.push(fixture.x);
        }
      }
    }
  }

  const boundaryClusterIds: number[] = [];
  let clusterId = 0;
  for (let i = 0; i < sectionBoundaries.length; i += 1) {
    if (i > 0) {
      const previousBoundary = sectionBoundaries[i - 1];
      const currentBoundary = sectionBoundaries[i];
      if (currentBoundary.left > previousBoundary.right + SECTION_X_TOLERANCE) {
        clusterId += 1;
      }
    }
    boundaryClusterIds.push(clusterId);
  }

  const sortedSectionWidths = sectionBoundaries.map(getSectionWidth).sort((a, b) => a - b);
  const medianSectionWidth = sortedSectionWidths[Math.floor(sortedSectionWidths.length / 2)] ?? 0;
  const MIN_MERGEABLE_SECTION_WIDTH = SECTION_X_TOLERANCE * 5;

  if (medianSectionWidth > MIN_MERGEABLE_SECTION_WIDTH) {
    const clusterSizeBeforeMerge = new Map<number, number>();
    for (const clusterKey of boundaryClusterIds) {
      clusterSizeBeforeMerge.set(clusterKey, (clusterSizeBeforeMerge.get(clusterKey) ?? 0) + 1);
    }

    for (let i = 1; i < sectionBoundaries.length; i += 1) {
      if (boundaryClusterIds[i] === boundaryClusterIds[i - 1]) {
        continue;
      }
      const gap = sectionBoundaries[i].left - sectionBoundaries[i - 1].right;
      const sourceClusterId = boundaryClusterIds[i];
      const targetClusterId = boundaryClusterIds[i - 1];
      const sourceCount = clusterSizeBeforeMerge.get(sourceClusterId) ?? 0;
      const targetCount = clusterSizeBeforeMerge.get(targetClusterId) ?? 0;

      const isTablerGap = Math.abs(gap - medianSectionWidth) <= SECTION_X_TOLERANCE;
      const minPillarGapWidth = Math.max(SECTION_X_TOLERANCE * 20, medianSectionWidth * 0.25);
      const isPillarGap =
        gap >= minPillarGapWidth &&
        gap < medianSectionWidth - SECTION_X_TOLERANCE &&
        Math.min(sourceCount, targetCount) >= 2;

      if (isTablerGap || isPillarGap) {
        for (let j = i; j < boundaryClusterIds.length; j += 1) {
          if (boundaryClusterIds[j] === sourceClusterId) {
            boundaryClusterIds[j] = targetClusterId;
          }
        }

        clusterSizeBeforeMerge.set(
          targetClusterId,
          (clusterSizeBeforeMerge.get(targetClusterId) ?? 0) + sourceCount,
        );
        clusterSizeBeforeMerge.delete(sourceClusterId);
      }
    }
  }

  const clusterBoundaryCount = new Map<number, number>();
  for (const clusterKey of boundaryClusterIds) {
    clusterBoundaryCount.set(clusterKey, (clusterBoundaryCount.get(clusterKey) ?? 0) + 1);
  }
  let mainClusterId = 0;
  let maxCount = 0;
  for (const [clusterKey, count] of clusterBoundaryCount.entries()) {
    if (count > maxCount) {
      maxCount = count;
      mainClusterId = clusterKey;
    }
  }

  const nonMainCount = sectionBoundaries.length - maxCount;
  const mainClusterIsDominant = maxCount > nonMainCount;

  const floatingBoundaryIndices = new Set<number>();
  if (mainClusterIsDominant) {
    for (let i = 0; i < boundaryClusterIds.length; i += 1) {
      if (boundaryClusterIds[i] !== mainClusterId) {
        floatingBoundaryIndices.add(i);
      }
    }
  }

  const sectionFixtures = new Map<number, PreviewPsaFixture[]>();
  const fixturesToAssign = [...normalFixtures];

  while (fixturesToAssign.length > 0) {
    const shelf = fixturesToAssign.shift();
    if (!shelf) {
      continue;
    }

    const sectionIndex = findSectionIndexForFixture(sectionBoundaries, shelf);
    const sectionBoundary = sectionBoundaries[sectionIndex];
    const nextSectionBoundary = sectionBoundaries[sectionIndex + 1];
    const splitPiece = nextSectionBoundary
      ? splitShelfAcrossSectionBoundary(
          shelf,
          sectionBoundary.right,
          getSectionWidth(nextSectionBoundary),
          options.minSectionOverlapRatio ?? DEFAULT_MIN_SECTION_OVERLAP_RATIO,
        )
      : null;
    if (splitPiece) {
      fixturesToAssign.push(splitPiece);
    } else {
      shelf.can_combine = shelf.can_combine ?? FixtureCanCombine.NONE;
    }

    sectionFixtures.set(sectionIndex, [...(sectionFixtures.get(sectionIndex) ?? []), shelf]);
  }

  const shelves: PreviewPsaFixture[] = [];
  const floatingSectionNums = new Set<number>();
  let sectionNumber = 0;

  for (let i = 0; i < sectionBoundaries.length; i += 1) {
    const sectionBoundary = sectionBoundaries[i];
    const shelvesInSection = collapseCompositeShelfRows(
      (sectionFixtures.get(i) ?? []).sort(
        (left, right) =>
          right.shelf_y - left.shelf_y ||
          left.x - right.x ||
          left.width - right.width ||
          (left.name ?? "").localeCompare(right.name ?? ""),
      ),
      sectionBoundary,
    );
    if (shelvesInSection.length === 0) {
      continue;
    }

    sectionNumber += 1;
    const sectionWidth = getSectionWidth(sectionBoundary);

    if (floatingBoundaryIndices.has(i)) {
      floatingSectionNums.add(sectionNumber);
    }

    for (let j = 0; j < shelvesInSection.length; j += 1) {
      const shelf = shelvesInSection[j];
      shelf.section_num = sectionNumber;
      shelf.shelf_num = j + 1;
      shelf.section_width = sectionWidth;
      shelf.section_x = sectionBoundary.left;
      shelves.push(shelf);
    }
  }

  if (explicitFloatingFixtures.length > 0) {
    const explicitFloatingByStart = new Map<number, PreviewPsaFixture[]>();
    for (const fixture of explicitFloatingFixtures) {
      const startX = explicitFloatingStartXsArray.find((startValue) =>
        isWithinSectionTolerance(fixture.x, startValue),
      );
      if (startX !== undefined) {
        const group = explicitFloatingByStart.get(startX) ?? [];
        group.push(fixture);
        explicitFloatingByStart.set(startX, group);
      }
    }

    for (const [, groupFixtures] of [...explicitFloatingByStart.entries()].sort(
      ([left], [right]) => left - right,
    )) {
      const sortedGroup = groupFixtures.sort(
        (left, right) =>
          right.shelf_y - left.shelf_y ||
          left.x - right.x ||
          left.width - right.width ||
          (left.name ?? "").localeCompare(right.name ?? ""),
      );
      sectionNumber += 1;
      floatingSectionNums.add(sectionNumber);
      const sectionWidth = sortedGroup.reduce(
        (width, fixture) => Math.max(width, fixture.width),
        0,
      );

      for (let j = 0; j < sortedGroup.length; j += 1) {
        const shelf = sortedGroup[j];
        shelf.section_num = sectionNumber;
        shelf.shelf_num = j + 1;
        shelf.section_width = sectionWidth;
        shelf.can_combine = shelf.can_combine ?? FixtureCanCombine.NONE;
        shelves.push(shelf);
      }
    }
  }

  return { fixtures: shelves, floatingSectionNums };
};
